import { Field, FieldArrayRenderProps } from "@progress/kendo-react-form";
import React from "react";
import { Card, GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import { MdDelete } from "react-icons/md";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import {
  requiredValidator,
  skipValidator,
} from "../../components/formFields/CommonValidator";
import { ICurrency } from "../currency/currencyModel";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { ISightseen } from "../sightseen/sightseenModel";
import IconButton from "../../components/common/IconButton";
import { setFITSightSeenDeleteIndex } from "./FITBookingSlice";

const FITBookingSightseenArray = (
  fieldArrayRenderProps: FieldArrayRenderProps
) => {
  const dispatch = useAppDispatch();
  const FITSightSeenDeleteIndex = useAppSelector(
    (state) => state.FITBooking.FITSightSeenDeleteIndex
  );
  const SightseenList = useAppSelector(
    (state) => state.sightseen.SightseenList
  );
  const CurrencyList = useAppSelector((state) => state.currency.CurrencyList);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleOpenDeleteDialog = (index: number) => {
    dispatch(openDialog("fitSightseenDeleteDialog"));
    dispatch(setFITSightSeenDeleteIndex(index));
  };

  const handleSightseenChange = (event: any, index: number) => {
    const sightSeen: any = SightseenList?.filter(
      (e: any) => e.ID == event?.value
    );
    if (sightSeen?.length > 0) {
      fieldArrayRenderProps?.formRenderProps.onChange(
        `SightSeen.${index}.Rate`,
        { value: sightSeen[0]?.Rate }
      );
    }
  };

  const handleDeleteAction = React.useCallback(
    (index: number) => {
      fieldArrayRenderProps.onRemove({ index: index });
      dispatch(closeDialog());
      dispatch(setFITSightSeenDeleteIndex(-1));
      if (
        !fieldArrayRenderProps.value ||
        fieldArrayRenderProps?.value?.length <= 1
      ) {
        fieldArrayRenderProps?.formRenderProps.onChange("SightSeenCurrencyID", {
          value: 1,
        });
        fieldArrayRenderProps?.formRenderProps.onChange(
          "SightSeenConversionRate",
          {
            value: 1,
          }
        );
      }
    },
    [fieldArrayRenderProps]
  );

  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps.onPush({
      value: {
        Title: "",
        TotalAmount: null,
      },
    });
  }, [fieldArrayRenderProps]);

  return (
    <GridLayout cols={[{ width: "100%" }]}>
      <GridLayoutItem
        style={{
          display: "flex",
          gap: 10,
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <div style={{ display: "flex", gap: 10 }}>
          <Field
            id={"SightSeenCurrencyID"}
            name={"SightSeenCurrencyID"}
            label={"Currency"}
            placeholder="Currency"
            component={FormSelectionField}
            validator={
              fieldArrayRenderProps.value &&
              fieldArrayRenderProps?.value?.length > 0
                ? requiredValidator
                : skipValidator
            }
            astrike={
              fieldArrayRenderProps.value &&
              fieldArrayRenderProps?.value?.length > 0
            }
            onChange={(e) => {
              if (e?.value) {
                const conversionRate =
                  CurrencyList.find(
                    (currency: ICurrency) => currency.ID === e?.value
                  )?.CurrencyConversion || "1";

                fieldArrayRenderProps?.formRenderProps.onChange(
                  "SightSeenConversionRate",
                  {
                    value: +conversionRate,
                  }
                );
              } else {
                fieldArrayRenderProps?.formRenderProps.onChange(
                  "SightSeenConversionRate",
                  {
                    value: 1,
                  }
                );
              }
            }}
            options={CurrencyList?.map((currency: ICurrency) => {
              return {
                value: currency.ID,
                label: currency.CurrencyName,
              };
            })}
          />
          <Field
            wrapperClassName="right-aligned-field"
            name="SightSeenConversionRate"
            label="Conversion Rate"
            placeholder="0"
            component={FormNumericTextField}
            disabled={
              !fieldArrayRenderProps.value ||
              fieldArrayRenderProps?.value?.length < 1
            }
            validator={
              fieldArrayRenderProps.value &&
              fieldArrayRenderProps?.value?.length > 0
                ? requiredValidator
                : skipValidator
            }
            astrike={
              fieldArrayRenderProps.value &&
              fieldArrayRenderProps?.value?.length > 0
            }
            min={0}
          />
          <Field
            wrapperClassName="right-aligned-field"
            name="SightSeenMarkup"
            label="SightSeen Markup"
            placeholder="0"
            component={FormNumericTextField}
            min={0}
          />
        </div>
        {(!fieldArrayRenderProps.value ||
          fieldArrayRenderProps?.value?.length < 1) && (
          <div>
            <Button
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={pushElementInarray}
              type="button"
              style={{ marginTop: 35 }}
            >
              Add SightSeen
            </Button>
          </div>
        )}
      </GridLayoutItem>
      {/* {!fieldArrayRenderProps.value || fieldArrayRenderProps?.value?.length < 1
        ? pushElementInarray()
        : null} */}
      {dialogName === "fitSightseenDeleteDialog" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete SightSeen"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(FITSightSeenDeleteIndex)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 &&
        fieldArrayRenderProps.value.map((SightSeen: any, index: number) => (
          <GridLayoutItem key={index}>
            <Card className="fieldarray-card">
              <GridLayout
                style={{ marginRight: 30 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "30%" },
                  { width: "30%" },
                  { width: "30%" },
                  { width: "10%" },
                ]}
              >
                <GridLayoutItem>
                  <Field
                    wrapperClassName="w-100"
                    id={"SightSeenID"}
                    name={`SightSeen.${index}.SightSeenID`}
                    label={"SightSeen"}
                    placeholder="SightSeen"
                    component={FormSelectionField}
                    astrike={true}
                    validator={requiredValidator}
                    onChange={(e: any) => handleSightseenChange(e, index)}
                    options={SightseenList?.map((sightseen: ISightseen) => {
                      return {
                        value: sightseen?.ID,
                        label: sightseen?.SightseenName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name={`SightSeen.${index}.Rate`}
                    label="Rate"
                    placeholder="0"
                    min={0}
                    component={FormNumericTextField}
                    validator={
                      fieldArrayRenderProps?.value[index]?.Title
                        ? requiredValidator
                        : skipValidator
                    }
                  />
                </GridLayoutItem>
                <GridLayoutItem className="text-end" colSpan={2}>
                  <IconButton
                    onClick={() => handleOpenDeleteDialog(index)}
                    // disabled={fieldArrayRenderProps.value.length === 1}
                    themeColor={"error"}
                    size={"small"}
                    fillMode={"solid"}
                    type="button"
                    style={{
                      height: 30,
                      width: 30,
                      marginTop: 35,
                    }}
                    icon={MdDelete}
                    iconClassName="absolute-position"
                    iconStyle={{ fontSize: "16px" }}
                  />
                </GridLayoutItem>
              </GridLayout>
            </Card>
          </GridLayoutItem>
        ))}

      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps?.value?.length > 0 && (
          <GridLayoutItem style={{ textAlign: "end", marginBottom: 10 }}>
            <Button
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={pushElementInarray}
              type="button"
            >
              Add SightSeen
            </Button>
          </GridLayoutItem>
        )}
    </GridLayout>
  );
};

export default FITBookingSightseenArray;
